/**
 * Session composable — owns the auth state for the SPA.
 *
 * Uses a tiny module-level reactive store (no Pinia) to share state across
 * components without extra dependencies.
 */

import { reactive, computed } from 'vue';
import { api, ApiCallError } from '@/services/api';
import type { SessionResponse } from '@/types/api';

interface SessionState {
  authenticated: boolean;
  username: string | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

const state = reactive<SessionState>({
  authenticated: false,
  username: null,
  isAdmin: false,
  loading: false,
  error: null,
});

export function useSession() {
  const isAuthenticated = computed(() => state.authenticated);

  async function refresh(): Promise<boolean> {
    state.loading = true;
    state.error = null;
    try {
      const res: SessionResponse = await api.session();
      state.authenticated = res.authenticated;
      state.username = res.username ?? null;
      state.isAdmin = Boolean(res.is_admin);
      return state.authenticated;
    } catch {
      state.authenticated = false;
      state.username = null;
      state.isAdmin = false;
      return false;
    } finally {
      state.loading = false;
    }
  }

  async function login(username: string, password: string): Promise<void> {
    state.loading = true;
    state.error = null;
    try {
      const res = await api.login(username, password);
      state.authenticated = true;
      state.username = res.username;
      // Probe the session so we capture is_admin from /session.
      await refresh();
    } catch (e) {
      state.authenticated = false;
      state.username = null;
      state.isAdmin = false;
      state.error = e instanceof ApiCallError ? e.detail : 'Login failed';
      throw e;
    } finally {
      state.loading = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      state.authenticated = false;
      state.username = null;
      state.isAdmin = false;
    }
  }

  return {
    state,
    isAuthenticated,
    refresh,
    login,
    logout,
  };
}
